import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../../services/api';
import { apiGet } from '../../../../api/axiosClient';
import { useRealTimeChat } from '../../../chat/context/RealTimeChatContext';

export const analysisSteps = [
    { percent: 10, title: 'Analyzing document structure and extracting text...', desc: '' },
    { percent: 30, title: 'Initial document analysis complete...', desc: '' },
    { percent: 40, title: 'Processing document layout and formatting...', desc: '' },
    { percent: 50, title: 'Applying AI classification to extracted content...', desc: '' },
    { percent: 55, title: 'Extracting key project requirements and metadata...', desc: '' },
    { percent: 70, title: 'Synthesizing strategic context and deliverables...', desc: '' },
    { percent: 85, title: 'Drafting comprehensive analytical chapters...', desc: '' },
    { percent: 100, title: 'Analysis and extraction finalized successfully!', desc: '' }
];

const formatIndian = (numStr) => {
    if (!numStr) return '';
    let lastThree = numStr.substring(numStr.length - 3);
    let otherDigits = numStr.substring(0, numStr.length - 3);
    if (otherDigits !== '') {
        lastThree = ',' + lastThree;
    }
    return otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree;
};

const formatInternational = (numStr) => {
    if (!numStr) return '';
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const formatValuation = (value, curr) => {
    if (value === undefined || value === null) return '';
    const rawVal = String(value).replace(/[^0-9.]/g, '');
    const dotIndex = rawVal.indexOf('.');
    let integerPart = rawVal;
    let decimalPart = '';
    if (dotIndex !== -1) {
        integerPart = rawVal.slice(0, dotIndex);
        decimalPart = rawVal.slice(dotIndex + 1);
    }

    let formattedInteger = '';
    if (curr === 'IND') {
        formattedInteger = formatIndian(integerPart);
    } else {
        formattedInteger = formatInternational(integerPart);
    }

    let result = formattedInteger;
    if (dotIndex !== -1) {
        result += '.' + decimalPart;
    }
    return result;
};

export const useRFPIntake = () => {
    const navigate = useNavigate();
    const { subscribeToMessages } = useRealTimeChat();
    const fileInputRef = useRef(null);
    const [files, setFiles] = useState([]);
    const [rawFiles, setRawFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [analysisError, setAnalysisError] = useState(null);
    const [analysisProgress, setAnalysisProgress] = useState(0);
    const [activeStepIndex, setActiveStepIndex] = useState(0);
    const [currentRunId, setCurrentRunId] = useState(null);
    const [steps, setSteps] = useState([]);

    const [queryDate, setQueryDate] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [valuation, setValuation] = useState('');
    const [currency, setCurrency] = useState('AED');
    const [toast, setToast] = useState(null);
    const [alertModal, setAlertModal] = useState({ show: false, message: '', stageCode: null, rfpId: null, rfpResData: null });

    const [fetchingGoNoGo, setFetchingGoNoGo] = useState(false);
    const [goNoGoData, setGoNoGoData] = useState(null);
    const [analysisRfpId, setAnalysisRfpId] = useState(null);
    const [analysisFullData, setAnalysisFullData] = useState(null);
    const [activeTab, setActiveTab] = useState('client-info');
    const [updatingDecision, setUpdatingDecision] = useState(false);
    const [selectedDecision, setSelectedDecision] = useState(null);

    // Refs to hold latest values so WS handler closure always has fresh data
    // without needing to re-subscribe every time these change.
    const isAnalyzingRef = useRef(false);
    const analysisRfpIdRef = useRef(null);
    const currentRunIdRef = useRef(null);
    const fetchGoNoGoDataRef = useRef(null);
    const isCompletionTriggeredRef = useRef(false);

    // --- Template & Project selection state ---
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [projects, setProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [selectedProjectIds, setSelectedProjectIds] = useState([]);
    const [selectAllProjects, setSelectAllProjects] = useState(false);

    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 4000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    // Fetch templates and projects on mount
    useEffect(() => {
        const fetchTemplates = async () => {
            setTemplatesLoading(true);
            try {
                const res = await apiGet('/templates');
                const data = res.data?.templates || [];
                setTemplates(data.map(t => ({
                    id: t.template_id,
                    name: t.name || t.client_name || 'Unnamed Template',
                })));
            } catch (e) {
                console.error('Failed to fetch templates:', e);
            } finally {
                setTemplatesLoading(false);
            }
        };

        const fetchProjects = async () => {
            setProjectsLoading(true);
            try {
                let res;
                try {
                    res = await api.get('/experience');
                } catch (err) {
                    res = await api.get('/projects');
                }
                const data = res.data?.projects || res.data || [];
                setProjects(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error('Failed to fetch projects:', e);
            } finally {
                setProjectsLoading(false);
            }
        };

        fetchTemplates();
        fetchProjects();
    }, []);

    const fetchGoNoGoData = async (rfp_id) => {
        if (!rfp_id) {
            console.error('[fetchGoNoGoData] Called with null rfp_id – skipping.');
            return;
        }
        try {
            setFetchingGoNoGo(true);
            console.log(`📡 [GoNoGo] Fetching RFP data for: ${rfp_id}`);
            const rfpRes = await api.get(`/rfp/${rfp_id}`);
            setAnalysisFullData(rfpRes.data);

            const goNoGoRes = await api.get(`/extraction/v1/${rfp_id}/go-no-go`);
            console.log(`✅ [GoNoGo] Data received for: ${rfp_id}`);
            setGoNoGoData(goNoGoRes.data);
        } catch (rfpErr) {
            console.error('Error fetching go/no-go:', rfpErr);
            setToast({ message: "Analysis complete, but failed to fetch Go/No-Go data.", type: 'error' });
            setGoNoGoData({ error: 'Failed to fetch Go/No-Go decision.' });
        } finally {
            setFetchingGoNoGo(false);
        }
    };

    // Keep refs in sync with latest state so the WS handler can read them
    // without ever becoming stale. This avoids having to re-subscribe on every
    // state change (which caused early EXTRACTION_PROGRESS events to be dropped).
    useEffect(() => { analysisRfpIdRef.current = analysisRfpId; }, [analysisRfpId]);
    useEffect(() => { currentRunIdRef.current = currentRunId; }, [currentRunId]);
    useEffect(() => { fetchGoNoGoDataRef.current = fetchGoNoGoData; }, [fetchGoNoGoData]);

    // Subscribe ONCE on mount – handler always reads from refs so it never goes stale.
    // Previously the subscription was gated on `analysisRfpId` being truthy, which
    // meant early progress messages (sent by the BE before the POST response returned
    // and before analysisRfpId was set in state) were silently dropped.
    useEffect(() => {
        const handleMessage = (payloadObj) => {
            const type = payloadObj.type || payloadObj.event;

            // Unwrap nested payload – BE may send { type, payload: {...} } or flat { type, rfp_id, ... }
            const data = payloadObj.payload || payloadObj.data || {};
            // Merge: prefer nested fields but fall back to top-level ones
            const payload = Object.keys(data).length > 0 ? { ...payloadObj, ...data } : payloadObj;

            const payloadRfpId = payload?.rfp_id || payload?.rfpId || payload?.id;
            const payloadRunId = payload?.run_id;

            // If we are actively analyzing on this screen:
            if (isAnalyzingRef.current) {
                // If we haven't bound to an rfp_id yet and this event has an rfp_id, bind to it!
                if (!analysisRfpIdRef.current && payloadRfpId) {
                    console.log(`🎯 [WS INTAKE] Auto-binding session to incoming rfp_id: ${payloadRfpId}`);
                    analysisRfpIdRef.current = payloadRfpId;
                    setAnalysisRfpId(payloadRfpId);
                }
            } else if (!analysisRfpIdRef.current && !currentRunIdRef.current) {
                // Not in an active analysis session and no session ID being tracked
                return;
            }

            const latestRfpId = analysisRfpIdRef.current;
            const latestRunId = currentRunIdRef.current;

            const isMatchingRfp =
                (latestRfpId && payloadRfpId && String(payloadRfpId).toLowerCase() === String(latestRfpId).toLowerCase()) ||
                (latestRunId && payloadRunId && String(payloadRunId).toLowerCase() === String(latestRunId).toLowerCase()) ||
                (isAnalyzingRef.current && (!latestRfpId || !payloadRfpId));

            console.log(`📡 [WS INTAKE] type=${type} payloadRfp=${payloadRfpId} activeRfp=${latestRfpId} match=${isMatchingRfp}`);

            if (type === 'EXTRACTION_PROGRESS' && isMatchingRfp) {
                const rawProgress = payload?.progress ?? payload?.progress_percent ?? payload?.percent;
                if (rawProgress !== undefined && rawProgress !== null) {
                    const num = Number(rawProgress);
                    if (!isNaN(num)) {
                        setAnalysisProgress(prev => Math.max(prev, num));
                    }
                }

                const stepName = String(payload?.step || payload?.stage || '').toUpperCase();
                // Accept any of the common field names the BE might use for the step description
                const currentMessage =
                    payload?.message ||
                    payload?.step_message ||
                    payload?.current_step ||
                    payload?.status_message ||
                    payload?.status ||
                    payload?.description ||
                    '';

                const isCompleted =
                    (rawProgress !== undefined && Number(rawProgress) >= 100) ||
                    stepName === 'COMPLETED' ||
                    stepName === 'EXTRACTION_COMPLETED';

                if (currentMessage || stepName) {
                    const displayTitle = currentMessage || `${stepName} in progress...`;
                    const currentProgressNum = rawProgress !== undefined && rawProgress !== null ? Number(rawProgress) : undefined;

                    setSteps(prevSteps => {
                        if (prevSteps.length === 0) {
                            return [{
                                id: `${stepName || 'step'}_${currentProgressNum ?? 0}_${Date.now()}`,
                                progress: currentProgressNum,
                                title: displayTitle,
                                at: Date.now(),
                                step: stepName,
                                isDone: isCompleted
                            }];
                        }

                        const lastStep = prevSteps[prevSteps.length - 1];

                        // If the message is the same as the last step, update it without duplicating
                        if (lastStep.title === displayTitle) {
                            return prevSteps.map((s, idx) =>
                                idx === prevSteps.length - 1
                                    ? { ...s, progress: currentProgressNum ?? s.progress, step: stepName || s.step, isDone: isCompleted }
                                    : s
                            );
                        }

                        // Otherwise mark previous steps as done and append new active card
                        const updatedPrev = prevSteps.map(s => ({ ...s, isDone: true }));
                        return [
                            ...updatedPrev,
                            {
                                id: `${stepName || 'step'}_${currentProgressNum ?? 0}_${Date.now()}`,
                                progress: currentProgressNum,
                                title: displayTitle,
                                at: Date.now(),
                                step: stepName,
                                isDone: isCompleted
                            }
                        ];
                    });
                }

                if (isCompleted) {
                    isAnalyzingRef.current = false;
                    setSteps(prevSteps => prevSteps.map(s => ({ ...s, isDone: true })));
                    if (!isCompletionTriggeredRef.current) {
                        isCompletionTriggeredRef.current = true;
                        const rfpIdToFetch = payloadRfpId || latestRfpId;
                        console.log(`🏁 [WS INTAKE] Analysis complete (progress=${rawProgress ?? 100}). Fetching GoNoGo for rfp=${rfpIdToFetch}`);
                        if (fetchGoNoGoDataRef.current) fetchGoNoGoDataRef.current(rfpIdToFetch);
                    }
                    setCurrentRunId(null);
                }
            } else if (type === 'EXTRACTION_COMPLETED' && isMatchingRfp) {
                isAnalyzingRef.current = false;
                setAnalysisProgress(100);
                setSteps(prevSteps => {
                    const markedDone = prevSteps.map(s => ({ ...s, isDone: true }));
                    const finalMsg = payload?.message || 'Analysis and extraction finalized successfully!';
                    const alreadyHasFinal = markedDone.some(s => s.title === finalMsg || s.progress === 100);
                    if (alreadyHasFinal) {
                        return markedDone;
                    }
                    return [
                        ...markedDone,
                        {
                            id: `completed_100_${Date.now()}`,
                            progress: 100,
                            title: finalMsg,
                            at: Date.now(),
                            step: 'COMPLETED',
                            isDone: true
                        }
                    ];
                });
                if (!isCompletionTriggeredRef.current) {
                    isCompletionTriggeredRef.current = true;
                    const rfpIdToFetch = payloadRfpId || latestRfpId;
                    console.log(`🏁 [WS INTAKE] EXTRACTION_COMPLETED received. Fetching GoNoGo for rfp=${rfpIdToFetch}`);
                    if (fetchGoNoGoDataRef.current) fetchGoNoGoDataRef.current(rfpIdToFetch);
                }
                setCurrentRunId(null);
            } else if (type === 'EXTRACTION_FAILED' && isMatchingRfp) {
                isAnalyzingRef.current = false;
                setAnalyzing(false);
                const errMsg = payload?.error || payload?.message || 'Analysis failed.';
                setAnalysisError(errMsg);
                setToast({ message: errMsg || 'Analysis failed. Please try again.', type: 'error' });
                setCurrentRunId(null);
            }
        };

        const unsubscribe = subscribeToMessages(handleMessage);
        return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscribeToMessages]); // subscribe ONCE; IDs are read from refs

    useEffect(() => {
        if (valuation) {
            setValuation(formatValuation(valuation, currency));
        }
    }, [currency]);

    const handleValuationChange = (val) => {
        let clean = val.replace(/[^0-9.,]/g, '');
        const parts = clean.split('.');
        if (parts.length > 2) {
            clean = parts[0] + '.' + parts.slice(1).join('');
        }

        const raw = clean.replace(/,/g, '');
        const formatted = formatValuation(raw, currency);
        setValuation(formatted);
    };

    const processFiles = (fileList) => {
        if (fileList.length === 0) return;
        setRawFiles(fileList);
        const selectedFiles = fileList.map(file => ({
            id: Math.random().toString(36).substr(2, 9),
            name: file.name,
            size: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
            type: file.name.split('.').pop().toUpperCase(),
        }));
        setFiles(selectedFiles);
    };

    const handleFileChange = (e) => {
        processFiles(Array.from(e.target.files));
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFiles = Array.from(e.dataTransfer.files);
        if (droppedFiles.length > 0) processFiles(droppedFiles);
    };

    const handleRemoveFile = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setFiles([]);
        setRawFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleStartAnalysis = async () => {
        const cleanValuation = valuation.replace(/,/g, '').trim();
        if (!cleanValuation || !/^\d+(\.\d+)?$/.test(cleanValuation)) {
            setToast({ message: 'Please enter a valid numeric valuation.', type: 'error' });
            return;
        }

        if (!dueDate || !dueDate.trim()) {
            setToast({ message: 'Please select a Proposal Submission Deadline.', type: 'error' });
            return;
        }

        if (queryDate && dueDate && new Date(queryDate) > new Date(dueDate)) {
            setToast({ message: 'Query Submission date must be earlier than or equal to the Proposal Submission date.', type: 'error' });
            return;
        }

        isAnalyzingRef.current = true;
        analysisRfpIdRef.current = null;
        currentRunIdRef.current = null;
        setAnalyzing(true);
        setAnalysisError(null);
        setAnalysisProgress(0);
        setActiveStepIndex(0);
        setSteps([]);
        // Reset completion guard so a new upload can trigger GoNoGo again
        isCompletionTriggeredRef.current = false;

        try {
            const formData = new FormData();
            if (rawFiles && rawFiles.length > 0) {
                formData.append('file', rawFiles[0]);
            }
            formData.append('valuation', cleanValuation || '0');
            formData.append('currency', currency);
            if (dueDate) formData.append('manual_due_date', dueDate);
            if (queryDate) formData.append('query_deadline', queryDate);
            if (selectedTemplateId) formData.append('template_id', selectedTemplateId);
            if (selectAllProjects) {
                formData.append('project_ids', 'ALL');
            } else if (selectedProjectIds.length > 0) {
                formData.append('project_ids', selectedProjectIds.join(','));
            }

            const response = await api.post('/extraction/v1/extract', formData, {
                headers: { 'Content-Type': undefined }
            });

            console.log('📡 [POST /extract RESPONSE]', response.status, response.data);

            if (response.status === 200 || response.status === 201) {
                const resData = response.data || {};
                const rfp_id = resData.rfp_id || resData.id || resData.data?.rfp_id || resData.data?.id;
                const run_id = resData.run_id || resData.data?.run_id;

                if (rfp_id) {
                    analysisRfpIdRef.current = rfp_id;
                    setAnalysisRfpId(rfp_id);
                }
                if (run_id) {
                    currentRunIdRef.current = run_id;
                    setCurrentRunId(run_id);
                }
            } else {
                if (!analysisRfpIdRef.current) {
                    isAnalyzingRef.current = false;
                    setAnalyzing(false);
                    setToast({ message: "Analysis failed. Please try again.", type: 'error' });
                }
            }
        } catch (err) {
            console.error('❌ [POST /extract ERROR]', err);
            if (!analysisRfpIdRef.current) {
                isAnalyzingRef.current = false;
                setAnalyzing(false);
                setToast({ message: "Network error. Could not connect to the server.", type: 'error' });
            }
        }
    };

    const handleGoNoGoSubmit = (stageCode) => {
        const rfpId = analysisRfpId;
        setAlertModal({
            show: true,
            message: stageCode === 8
                ? "Are you sure you want to move the proposal forward for further preparation and submission activities?"
                : "Are you sure you want to discontinue further proposal activities?",
            stageCode: stageCode,
            rfpId: rfpId,
            isConfirmation: true
        });
    };

    const handleConfirmGoNoGo = async () => {
        const { stageCode, rfpId } = alertModal;
        if (!rfpId) return;

        setUpdatingDecision(true);
        try {
            await api.patch(`/rfp/${rfpId}/proposal-stage`, { stage: stageCode, notes: "" });

            setAlertModal({ show: false, message: '', stageCode: null });

            if (stageCode === 8) {
                const rfpRes = await api.get(`/rfp/${rfpId}`);
                navigate(`/proposals/analysis-result/${rfpId}`, {
                    state: { analysisData: rfpRes.data, fromUpload: true, files: rawFiles }
                });
            } else {
                navigate('/proposals');
            }
        } catch (err) {
            console.error('Error submitting Go/No-Go decision:', err);
            setToast({ message: "Failed to update decision stage.", type: 'error' });
        } finally {
            setUpdatingDecision(false);
        }
    };

    return {
        navigate, fileInputRef, files, isDragging, analyzing, analysisProgress, activeStepIndex, steps,
        queryDate, setQueryDate, dueDate, setDueDate, valuation, setValuation, currency, setCurrency,
        toast, fetchingGoNoGo, goNoGoData, activeTab, setActiveTab, updatingDecision, selectedDecision,
        alertModal, setAlertModal, rawFiles,
        handleFileChange, handleDragOver, handleDragLeave, handleDrop, handleRemoveFile, handleStartAnalysis,
        handleGoNoGoSubmit, handleConfirmGoNoGo,
        isFormValid: files.length > 0 && valuation.trim() !== '' && Boolean(dueDate && dueDate.trim()),
        handleValuationChange,
        // Template & project selection
        templates, templatesLoading, selectedTemplateId, setSelectedTemplateId,
        projects, projectsLoading, selectedProjectIds, setSelectedProjectIds,
        selectAllProjects, setSelectAllProjects,
        setTemplates,  // exposed so caller can refresh list after creating a new template
    };
};
